import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { Button } from '@radix-ui/themes';
import { DownloadIcon } from '@radix-ui/react-icons';
import SubmissionPDFDocument from './SubmissionPDFDocument';
import { PDFExportProps } from './types';

const PDFExportButton: React.FC<PDFExportProps> = ({ 
  submissionData, 
  isTeacherView = false,
  className = '',
  variant = 'solid',
  size = '3'
}) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Generate filename based on context
  const generateFilename = (data = submissionData): string => {
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');

    const className = (data.classId?.name || data.class?.name || 'Unknown_Class').replace(/[^a-zA-Z0-9]/g, '_');
    
    // Get student name from submission data or localStorage
    let studentName = data.student?.name;
    if (!studentName) {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        studentName = user.name || 'Current_Student';
      } else {
        studentName = 'Current_Student';
      }
    }
    
    if (isTeacherView && studentName) {
      const cleanStudentName = studentName.replace(/[^a-zA-Z0-9]/g, '_');
      return `${cleanStudentName}_${className}_Submission_${date}.pdf`;
    } else {
      return `${className}_Submission_${date}.pdf`;
    }
  };

  // Handle PDF export
  const handleExport = async (): Promise<void> => {
    setIsGenerating(true);
    setError(null);

    try {
      // Get current user name from localStorage if student data is missing
      let enrichedSubmissionData = { ...submissionData };
      
      if (!submissionData.student) {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          enrichedSubmissionData.student = {
            id: user.userId || '',
            name: user.name || 'Current Student',
            email: user.email || ''
          };
        }
      }

      // Generate PDF document
      const pdfDocument = (
        <SubmissionPDFDocument
          submissionData={enrichedSubmissionData}
          isTeacherView={isTeacherView}
          generatedAt={new Date()}
        />
      );

      // Create PDF blob
      const blob = await pdf(pdfDocument).toBlob();
      
      // Generate filename and save
      const filename = generateFilename(enrichedSubmissionData);
      saveAs(blob, filename);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Clear error when user tries again
  const handleRetry = (): void => {
    setError(null);
    handleExport();
  };

  return (
    <div className={`pdf-export-container ${className}`}>
      <Button
        onClick={error ? handleRetry : handleExport}
        disabled={isGenerating}
        variant={variant}
        size={size}
        className={`
          bg-yellow-400 hover:bg-yellow-500 text-black font-semibold
          transition-colors duration-200 cursor-pointer
          disabled:opacity-50 disabled:cursor-not-allowed
          ${error ? 'border-red-500 border-2' : ''}
        `}
      >
        {isGenerating ? (
          <>
            {/* Loading spinner */}
            <div className="w-4 h-4 mr-2 border-2 border-black border-t-transparent rounded-full animate-spin" />
            Generating...
          </>
        ) : error ? (
          <>
            {/* Retry icon */}
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry PDF
          </>
        ) : (
          <>
            <DownloadIcon className="w-4 h-4 mr-2" />
            Export PDF
          </>
        )}
      </Button>
      
      {/* Error message */}
      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default PDFExportButton; 